import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../styles/LLMember/2Photobooth.css';

function PhotoSelection() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const allPhotos = state?.images || [];
  const selectedFrame = state?.selectedFrame;
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const canvasRef = useRef(null);

  const toggleSelect = (photo) => {
    if (selectedPhotos.includes(photo)) {
      setSelectedPhotos(prev => prev.filter(p => p !== photo));
    } else if (selectedPhotos.length < 4) {
      setSelectedPhotos(prev => [...prev, photo]);
    }
  };

  const goToResult = () => {
    navigate('/result/member', { state: { images: selectedPhotos, selectedFrame } });
  };

  // Use the same rendering approach as ResultScreen
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Detect mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
    console.log('Device detection - isMobile:', isMobile, 'userAgent:', navigator.userAgent);
    
    // Set initial canvas dimensions
    const singlePhotoWidth = 900;
    // Don't set height here - it will be set based on frame aspect ratio
    canvas.width = singlePhotoWidth;
    
    // Clear canvas with white background - will be reset when frame loads
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, 600); // temporary height

    // Helper function to calculate source crop dimensions for an image to fill target area
    function calculateCropDimensions(imgWidth, imgHeight, targetWidth, targetHeight) {
      const imgRatio = imgHeight / imgWidth;
      const targetRatio = targetHeight / targetWidth;
      let sourceX, sourceY, sourceWidth, sourceHeight;
      if (imgRatio > targetRatio) {
        // Image is taller than target - crop top and bottom
        sourceWidth = imgWidth;
        sourceHeight = imgWidth * targetRatio;
        sourceX = 0;
        sourceY = (imgHeight - sourceHeight) / 2;
      } else {
        // Image is wider than target - crop left and right
        sourceHeight = imgHeight;
        sourceWidth = imgHeight / targetRatio;
        sourceX = (imgWidth - sourceWidth) / 2;
        sourceY = 0;
      }
      return { sourceX, sourceY, sourceWidth, sourceHeight };
    }

    // Helper function to draw placeholders
    function drawPlaceholder(x, y, width, height) {
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(x, y, width, height);
      ctx.fillStyle = '#999';
      ctx.font = '32px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('', x + width/2, y + height/2);
    }

    // On mobile, use simpler frame loading approach
    if (isMobile) {
      console.log('Mobile detected, using mobile-optimized layout');
      
      // Load frame first to get proper dimensions
      const frameImg = new window.Image();
      frameImg.src = selectedFrame?.frameImage || '/img/LLMember/Yeonjun_LL.png';
      
      frameImg.onload = () => {
        console.log('Mobile frame loaded:', frameImg.src);
        
        // Scale down for mobile but keep reasonable resolution
        const maxWidth = Math.min(450, window.innerWidth - 40);
        const scaleFactor = maxWidth / frameImg.naturalWidth;
        const mobileWidth = Math.round(frameImg.naturalWidth * scaleFactor);
        const mobileHeight = Math.round(frameImg.naturalHeight * scaleFactor);
        
        canvas.width = mobileWidth;
        canvas.height = mobileHeight;
        
        console.log('Mobile canvas dimensions:', canvas.width, 'x', canvas.height);
        
        // Clear canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Define photo areas scaled for mobile
        const photoWidth = mobileWidth * 0.91;
        const photoHeight = photoWidth / (4/3);
        const photoX = (mobileWidth - photoWidth) / 2;
        const topMargin = mobileHeight * 0.055;
        const spacing = mobileHeight * 0.002;
        
        const photoAreas = [
          { x: photoX, y: topMargin, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 1, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 2, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 3, width: photoWidth, height: photoHeight }
        ];
        
        let imagesLoaded = 0;
        const totalImages = selectedPhotos.length;
        
        // Draw photos
        for (let i = 0; i < 4; i++) {
          const imgSrc = i < selectedPhotos.length ? selectedPhotos[i] : null;
          const area = photoAreas[i];
          
          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
              const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
                img.naturalWidth,
                img.naturalHeight,
                area.width,
                area.height
              );
              ctx.drawImage(
                img,
                sourceX, sourceY, sourceWidth, sourceHeight,
                area.x, area.y, area.width, area.height
              );
              
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                // Draw frame on top
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
              }
            };
            img.onerror = () => {
              drawPlaceholder(area.x, area.y, area.width, area.height);
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
              }
            };
          } else {
            drawPlaceholder(area.x, area.y, area.width, area.height);
          }
        }
        
        // If no images, draw frame immediately
        if (totalImages === 0) {
          ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
        }
      };
      
      frameImg.onerror = () => {
        console.error('Mobile frame loading failed, using simple layout');
        // Fallback to simple stacked layout
        const mobileWidth = Math.min(400, window.innerWidth - 40);
        const mobileHeight = Math.round(mobileWidth * 4 / 3);
        canvas.width = mobileWidth;
        canvas.height = mobileHeight * 4;
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        for (let i = 0; i < 4; i++) {
          const imgSrc = i < selectedPhotos.length ? selectedPhotos[i] : null;
          const y = i * mobileHeight;
          
          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
              ctx.drawImage(img, 0, y, mobileWidth, mobileHeight);
            };
          }
        }
        
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 3;
        ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
      };
      
      return; // Skip desktop frame loading
    }

    // First, draw the frame to establish the layout
    const frameImg = new window.Image();
    frameImg.src = selectedFrame?.frameImage || '/img/LLMember/Yeonjun_LL.png';
    
    console.log('Attempting to load frame image:', frameImg.src);
    
    frameImg.onload = () => {
      console.log('Frame image loaded successfully:', frameImg.src);
      // Calculate the proper frame dimensions to maintain aspect ratio
      const frameNaturalWidth = frameImg.naturalWidth;
      const frameNaturalHeight = frameImg.naturalHeight;
      const frameAspectRatio = frameNaturalHeight / frameNaturalWidth;
      
      // Calculate frame display size - preserve natural aspect ratio
      const maxFrameWidth = singlePhotoWidth;
      const maxFrameHeight = 1200; // Set a reasonable max height
      
      let frameDisplayWidth, frameDisplayHeight;
      
      if (frameAspectRatio > maxFrameHeight / maxFrameWidth) {
        // Frame is taller - constrain by height
        frameDisplayHeight = maxFrameHeight;
        frameDisplayWidth = frameDisplayHeight / frameAspectRatio;
      } else {
        // Frame is wider - constrain by width  
        frameDisplayWidth = maxFrameWidth;
        frameDisplayHeight = frameDisplayWidth * frameAspectRatio;
      }
      
      // Set canvas to exactly match frame dimensions - no stretching
      canvas.width = frameDisplayWidth;
      canvas.height = frameDisplayHeight;
      
      // Clear canvas with new dimensions
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Calculate photo areas to match the 4:3 aspect ratio used in the booth
      const photoAspectRatio = 4/3; // Same as booth webcam
      const photoWidth = frameDisplayWidth * 0.91; // Same as ConfirmLLMember
      const photoHeight = photoWidth / photoAspectRatio; // Maintain 4:3 aspect ratio
      const photoX = (frameDisplayWidth - photoWidth) / 2; // Center horizontally
      
      // Vertical positioning - evenly spaced photo areas
      const topMargin = frameDisplayHeight * 0.055; // Top margin
      const spacing = frameDisplayHeight * 0.002; // Space between photos
      
      const photoAreas = [
        { x: photoX, y: topMargin, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 1, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 2, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 3, width: photoWidth, height: photoHeight }
      ];

      // Track how many images have been loaded
      let imagesLoaded = 0;
      const totalImages = selectedPhotos.length;

      // Function to draw frame after all images are loaded
      const drawFrameOnTop = () => {
        ctx.drawImage(frameImg, 0, 0, frameDisplayWidth, frameDisplayHeight);
      };
      
      // Draw photos FIRST (behind the frame)
      const loadedImages = [];
      
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < selectedPhotos.length ? selectedPhotos[i] : null;
        const area = photoAreas[i];
        
        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth,
              img.naturalHeight,
              area.width,
              area.height
            );
            ctx.drawImage(
              img,
              sourceX, sourceY, sourceWidth, sourceHeight,
              area.x, area.y, area.width, area.height
            );
            
            loadedImages.push(i);
            // Only draw frame after ALL selected photos have loaded
            if (loadedImages.length === totalImages) {
              drawFrameOnTop();
            }
          };
          img.onerror = () => {
            drawPlaceholder(area.x, area.y, area.width, area.height);
            loadedImages.push(i);
            if (loadedImages.length === totalImages) {
              drawFrameOnTop();
            }
          };
        } else {
          drawPlaceholder(area.x, area.y, area.width, area.height);
        }
      }

      // If no images to load, draw frame immediately
      if (totalImages === 0) {
        drawFrameOnTop();
      }
    };
    
    frameImg.onerror = () => {
      console.error('Frame image failed to load at', selectedFrame?.frameImage);
      console.log('Falling back to simple layout');
      
      // If frame fails to load, fall back to original behavior
      const singlePhotoWidth = 900;
      const singlePhotoHeight = Math.round(singlePhotoWidth * 4 / 3);
      canvas.width = singlePhotoWidth;
      canvas.height = singlePhotoHeight * 4;
      
      // Clear canvas with new dimensions
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw photos normally and add a simple border
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < selectedPhotos.length ? selectedPhotos[i] : null;
        const y = i * singlePhotoHeight;
        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth,
              img.naturalHeight,
              singlePhotoWidth,
              singlePhotoHeight
            );
            ctx.drawImage(
              img,
              sourceX, sourceY, sourceWidth, sourceHeight,
              0, y, singlePhotoWidth, singlePhotoHeight
            );
          };
          img.onerror = () => {
            drawPlaceholder(0, y, singlePhotoWidth, singlePhotoHeight);
          };
        } else {
          drawPlaceholder(0, y, singlePhotoWidth, singlePhotoHeight);
        }
      }
      
      // Draw a simple border as fallback
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 10;
      ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
    };
  }, [selectedPhotos, selectedFrame]);

  return (
    <div className="photo-selection-container">
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Choose your favorite 4 photos</h2>
      <div className="selection-content">
        <div className="photo-grid-section">
          <div className="photo-gridTMRW">
            {allPhotos.map((photo, idx) => (
              <div key={idx} className="thumbnail-container">
                <img
                  src={photo}
                  alt={`Photo ${idx + 1}`}
                  className={`photo-thumbnail ${selectedPhotos.includes(photo) ? 'selected' : ''}`}
                  onClick={() => toggleSelect(photo)}
                />
                {selectedPhotos.includes(photo) && (
                  <div className="photo-number">
                    {selectedPhotos.indexOf(photo) + 1}
                  </div>
                )}
              </div>
            ))}

            {/* Fill remaining slots if less than 8 photos */}
            {Array.from({ length: Math.max(0, 8 - allPhotos.length) }).map((_, idx) => (
              <div key={`empty-${idx}`} className="thumbnail-container">
                <div className="empty-slot">
                </div>
              </div>
            ))}

            {/* Status card for selection status - positioned at the end */}
            <div className="thumbnail-container status-card">
              <div className="status-content">
                <div className="status-number">{selectedPhotos.length}/4</div>
                <div className="status-label">Selected</div>
              </div>
            </div>
          </div>

          {/* Button underneath photo thumbnails */}
          <button
            className="next-button"
            onClick={goToResult}
            disabled={selectedPhotos.length !== 4}
          >
            Create Photo Strip
          </button>
        </div>

        {/* Right side: frame preview with canvas */}
        <div className="frame-preview-container">
          <div className="frame-preview">
            <canvas 
              ref={canvasRef} 
              className="photo-strip"
              style={{
                maxWidth: '450px',
                width: '100%',
                height: 'auto'
              }}
            />
          </div>  
        </div>
      </div>
    </div>
  );
}

export default PhotoSelection;